import { useEffect, useState } from "react";
import { BouquetPicker } from "./BouquetPicker";
import { api, fmtBRL } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, MessageCircle, Copy } from "lucide-react";
import { Modal, Btn, Field, inputCls } from "./ui";

const welcome = (r, name) =>
  `Olá${name ? ` ${name.split(" ")[0]}` : ""}! Seu acesso foi ativado.\nUsuário: ${r.username}\nSenha: ${r.password}\nVálido até: ${new Date(r.expires_at).toLocaleDateString("pt-BR")}`;

export const NewClientDialog = ({ onClose, onSaved }) => {
  const [pkgs, setPkgs] = useState([]);
  const [f, setF] = useState({ package_id: "", name: "", whatsapp: "", email: "", note: "", bouquets: [] });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => {
    api.get("/new-client/packages").then((r) => { setPkgs(r.data); setF((x) => ({ ...x, package_id: r.data[0]?.id || "" })); })
      .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar pacotes"));
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const p = pkgs.find((x) => x.id === f.package_id);
  const screens = [...new Set(pkgs.map((x) => x.connections))];

  const create = async () => {
    if (!f.name.trim()) return toast.error("Informe o nome");
    setBusy(true);
    try {
      const { data } = await api.post("/new-client", f);
      setRes(data);
      toast.success(`Cliente criado no SYSLOG (wetester) · saldo ${data.credits ?? "?"} créditos`);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao criar cliente"); }
    setBusy(false);
  };

  if (res) {
    const msg = welcome(res, f.name);
    const wa = (res.client?.whatsapp || "").replace(/\D/g, "");
    return (
      <Modal testid="add-client-modal" title="Cliente criado no SYSLOG" onClose={onClose}>
        <pre data-testid="new-client-result" className="whitespace-pre-wrap rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 font-mono text-sm text-emerald-100">{msg}</pre>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Btn tone="ghost" data-testid="new-client-close" onClick={onClose}>Fechar</Btn>
          <Btn tone="ghost" data-testid="new-client-copy" onClick={() => navigator.clipboard.writeText(msg).then(() => toast.success("Copiado"))}>
            <span className="flex items-center gap-1.5"><Copy className="h-4 w-4" /> Copiar</span>
          </Btn>
          {wa && (
            <a data-testid="new-client-send-wa" href={`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-slate-950 hover:brightness-110">
              <MessageCircle className="h-4 w-4" /> Enviar no WhatsApp
            </a>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal testid="add-client-modal" title="Novo cliente · conta wetester" onClose={onClose} wide>
      {screens.map((s) => (
        <div key={s} className="mb-3">
          <div className="mb-1.5 text-[11px] uppercase tracking-wider text-slate-500">{s} tela{s > 1 ? "s" : ""}</div>
          <div className="grid grid-cols-4 gap-2">
            {pkgs.filter((x) => x.connections === s).map((x) => (
              <button key={x.id} data-testid={`new-pkg-${x.id}`} onClick={() => setF({ ...f, package_id: x.id })}
                className={`rounded-lg border p-2 text-left text-xs transition-colors ${f.package_id === x.id ? "border-sky-500 bg-sky-500/10 text-sky-200" : "border-[#1F293D] bg-[#111827] text-slate-300 hover:border-[#334155]"}`}>
                <div className="font-bold">{x.duration} {x.duration === 1 ? "mês" : "meses"}</div>
                <div className="text-slate-500">{fmtBRL(x.plan_price / 100)}</div>
              </button>
            ))}
          </div>
        </div>
      ))}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Nome *"><input data-testid="add-name" value={f.name} onChange={set("name")} className={inputCls} /></Field>
        <Field label="WhatsApp"><input data-testid="add-whatsapp" value={f.whatsapp} placeholder="(11) 99999-9999" onChange={set("whatsapp")} className={inputCls} /></Field>
        <Field label="E-mail"><input data-testid="add-email" value={f.email} onChange={set("email")} className={inputCls} /></Field>
        <Field label="Observação"><input data-testid="add-note" value={f.note} onChange={set("note")} className={inputCls} /></Field>
        <BouquetPicker prefix="add" value={f.bouquets} onChange={(b) => setF({ ...f, bouquets: b })} />
      </div>
      {p && (
        <div data-testid="new-client-confirm-text" className="mt-4 flex gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Criar <b>{p.name}</b> na conta <b>wetester</b>? Vai consumir <b>{p.credits} crédito{p.credits > 1 ? "s" : ""}</b>. Usuário e senha são gerados pelo SYSLOG.</span>
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
        <Btn data-testid="save-client-button" disabled={!p || busy} onClick={create}>{busy ? "Criando..." : "Criar no SYSLOG"}</Btn>
      </div>
    </Modal>
  );
};
